import React, { useState, useEffect, useMemo, useCallback } from "react";
import { api } from "../../api";
import { Icon, Spinner, useToast } from "../../components/ui";
import { derive, applyRefData } from "../admin/recordDerive";
import { ProductivityDrill } from "../superadmin/ProductivityDrill";

const STAGE_KEY = { SUBMISSION: "submissions", END_PROCESS: "endProcess", MODIFICATION: "modifications", MORE_INFO: "moreInfo" };
function mdyToDate(s) { const m = String(s || "").match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/); return m ? new Date(+m[3], +m[1] - 1, +m[2]) : null; }
function isoToDate(s) { const m = String(s || "").match(/^(\d{4})-(\d{2})-(\d{2})$/); return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null; }
const fmtRev = (n) => { const x = Math.round((Number(n) || 0) * 100) / 100; return x ? String(x) : "—"; };   // fractional Reviewed score (e.g. 9.25)
function quickRange(kind, anchor) {
  const t = anchor || (() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), n.getDate()); })();
  switch (kind) {
    case "today": return [t, t];
    case "yesterday": { const y = new Date(t); y.setDate(t.getDate() - 1); return [y, y]; }
    case "thisWeek": { const s = new Date(t); s.setDate(t.getDate() - ((t.getDay() + 6) % 7)); return [s, t]; }
    case "lastWeek": { const s = new Date(t); s.setDate(t.getDate() - ((t.getDay() + 6) % 7) - 7); const e = new Date(s); e.setDate(s.getDate() + 6); return [s, e]; }
    case "thisMonth": return [new Date(t.getFullYear(), t.getMonth(), 1), t];
    case "lastMonth": return [new Date(t.getFullYear(), t.getMonth() - 1, 1), new Date(t.getFullYear(), t.getMonth(), 0)];
    case "thisYear": return [new Date(t.getFullYear(), 0, 1), t];
    case "lastYear": return [new Date(t.getFullYear() - 1, 0, 1), new Date(t.getFullYear() - 1, 11, 31)];
    default: return [null, null];
  }
}
const QUICKS = [["today", "Today"], ["yesterday", "Yesterday"], ["thisWeek", "This week"], ["lastWeek", "Last week"], ["thisMonth", "This month"], ["lastMonth", "Last month"], ["thisYear", "This year"], ["lastYear", "Last year"]];

export function MyProductivity({ user, client }) {
  const toast = useToast();
  const me = user?.fullName || "";
  const [rows, setRows] = useState(null);
  const [group, setGroup] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [period, setPeriod] = useState("today");
  const [bizToday, setBizToday] = useState(null);   // server business day (rolls at the shift boundary) — timezone-proof
  useEffect(() => { api.businessToday().then((r) => { const d = isoToDate(r && r.today); if (d) setBizToday(d); }).catch(() => {}); }, []);
  const [drill, setDrill] = useState(null);

  const [reviewActs, setReviewActs] = useState([]);   // fractional Reviewed-effort events {user, day, weight, group}
  const load = useCallback(async () => {
    try {
      const [recs, ref, acts] = await Promise.all([api.myProductivity(), api.getRefData().catch(() => null), api.reviewActivity().catch(() => [])]);
      applyRefData(ref); setRows(recs); setReviewActs(acts || []);
    } catch (e) { toast(e.message, "err"); setRows([]); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  // Live view: silently re-pull your worked records every 10s so your numbers update on their own.
  // Filters are kept; paused while the tab is hidden or a drill-down is open.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.hidden || drill) return;
      Promise.all([api.myProductivity({ silent: true }), api.getRefData({ silent: true }).catch(() => null), api.reviewActivity({ silent: true }).catch(() => [])])
        .then(([recs, ref, acts]) => { applyRefData(ref); setRows(recs); setReviewActs(acts || []); })
        .catch(() => {});
    }, 10000);
    return () => clearInterval(id);
  }, [drill]);

  const events = useMemo(() => {
    const out = [];
    (rows || []).forEach((r) => {
      const d = derive(r);
      const base = { recordId: r.id, user: me, mrn: r.mrn, order: d.orders || r.orderName, group: d.location || "—", stat: d.clinicPriority === "STAT" };
      let hist = [];
      try { hist = r.workHistory ? JSON.parse(r.workHistory) : []; } catch { hist = []; }
      // Track reviewed spells across ALL rounds (any user) so a re-touch is scored right; emit only my rounds.
      // A user's first "Reviewed" round in a spell is a real keep (work); a later one in the same spell is a
      // 0.25 re-touch (via reviewActs) and must NOT inflate Work done / process. A non-reviewed round ends the spell.
      let prevReviewed = false; const keptThisSpell = new Set();
      hist.forEach((h) => {
        const isRev = h.status === "Reviewed";
        let retouch = false;
        if (isRev) {
          if (!prevReviewed) keptThisSpell.clear();
          const by = h.by || "";
          if (keptThisSpell.has(by)) retouch = true; else keptThisSpell.add(by);
          prevReviewed = true;
        } else { prevReviewed = false; keptThisSpell.clear(); }
        if (h.by === me) out.push({
          ...base, date: mdyToDate(h.date), dateStr: h.date || "", stage: h.stage, status: h.status,
          statusDate: h.statusDate || h.date || "", reason: h.reason || "", remarks: h.remarks || "",
          issueType: h.issueType, issueReason: h.issueReason || "", retouch,
        });
      });
    });
    return out;
  }, [rows, me]);

  const allGroups = useMemo(() => Array.from(new Set(events.map((e) => e.group).filter((g) => g && g !== "—"))).sort(), [events]);
  const [rFrom, rTo] = useMemo(() => {
    if (period === "custom") return [isoToDate(from), isoToDate(to)];
    if (period) return quickRange(period, bizToday);
    return [null, null];
  }, [period, from, to, bizToday]);

  // Week / month / custom → per-DATE rows; year → per-MONTH totals; today / yesterday / all-time → one total.
  const bucketMode = (period === "thisWeek" || period === "lastWeek" || period === "thisMonth" || period === "lastMonth" || period === "custom") ? "day"
    : (period === "thisYear" || period === "lastYear") ? "month" : null;
  const pad2 = (n) => String(n).padStart(2, "0");
  const bucketOf = (dt) => { if (!bucketMode || !dt) return ""; return bucketMode === "day" ? `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}` : `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}`; };
  const bucketLabel = (key) => { if (!key) return ""; const pp = key.split("-"); return bucketMode === "day" ? `${pp[1]}/${pp[2]}/${pp[0]}` : new Date(Number(pp[0]), Number(pp[1]) - 1, 1).toLocaleDateString([], { month: "short", year: "numeric" }); };
  const blank = () => ({ submissions: 0, endProcess: 0, modifications: 0, moreInfo: 0, stat: 0, approved: 0, paNotReq: 0, denied: 0, pending: 0, canceled: 0, withdraw: 0, reviewed: 0, clientIssue: 0, patientIssue: 0, productionIssue: 0, deniedL: [], canceledL: [], withdrawL: [], clientL: [], patientL: [], productionL: [] });

  const report = useMemo(() => {
    const byBkt = new Map();
    const get = (bkt) => { if (!byBkt.has(bkt)) { const nb = blank(); nb.__bucket = bkt; byBkt.set(bkt, nb); } return byBkt.get(bkt); };
    events.forEach((e) => {
      if (client && e.group !== client) return;
      if (rFrom && (!e.date || e.date < rFrom)) return;
      if (rTo && (!e.date || e.date > rTo)) return;
      const b = get(bucketOf(e.date));
      // A reviewed re-touch is not new work — keep it out of the Work done (process) and STAT columns.
      if (!e.retouch) { const sk = STAGE_KEY[e.stage]; if (sk) b[sk] += 1; if (e.stat) b.stat += 1; }
      switch (e.status) {
        case "Approved": b.approved += 1; break;
        case "PA Not Required": b.paNotReq += 1; break;
        case "Denied": b.denied += 1; b.deniedL.push(e); break;
        case "Pending": b.pending += 1; break;
        case "Canceled": b.canceled += 1; b.canceledL.push(e); break;
        case "Withdraw": b.withdraw += 1; b.withdrawL.push(e); break;
        // Faxed / Submitted via call = submitted to the payor, awaiting a response → counted as Pending (no separate column).
        case "Faxed": case "Submitted via call": b.pending += 1; break;
        // Reviewed comes from reviewActs below (1.0 new keep, 0.25 later re-touch).
        case "Reviewed": break;
        default: break;
      }
      if (e.issueType === "Client") { b.clientIssue += 1; b.clientL.push(e); }
      else if (e.issueType === "Patient") { b.patientIssue += 1; b.patientL.push(e); }
      else if (e.issueType === "Production") { b.productionIssue += 1; b.productionL.push(e); }
    });
    (reviewActs || []).forEach((a) => {
      if (a.user !== me) return;
      if (client && a.group !== client) return;
      const dt = isoToDate(a.day);
      if (!dt) return;
      if (rFrom && dt < rFrom) return;
      if (rTo && dt > rTo) return;
      get(bucketOf(dt)).reviewed += (Number(a.weight) || 0);
    });
    return Array.from(byBkt.values()).map((b) => {
      const decided = b.approved + b.paNotReq + b.denied + b.pending + b.canceled + b.withdraw + b.reviewed;
      return { bucket: b.__bucket, ...b, decided, approvalPct: decided ? (b.approved / decided) * 100 : 0 };
    }).sort((a, b) => (a.bucket < b.bucket ? -1 : a.bucket > b.bucket ? 1 : 0));
  }, [events, reviewActs, client, rFrom, rTo, bucketMode, me]); // eslint-disable-line

  // Combined totals (for the single-row view and the header total).
  const s = useMemo(() => {
    const t = blank(); let approved = 0, decided = 0;
    report.forEach((r) => {
      ["submissions", "endProcess", "modifications", "moreInfo", "stat", "approved", "paNotReq", "denied", "pending", "canceled", "withdraw", "reviewed", "clientIssue", "patientIssue", "productionIssue"].forEach((k) => { t[k] += r[k]; });
      ["deniedL", "canceledL", "withdrawL", "clientL", "patientL", "productionL"].forEach((k) => { t[k] = t[k].concat(r[k]); });
      approved += r.approved; decided += r.decided;
    });
    return { ...t, decided, approvalPct: decided ? (approved / decided) * 100 : 0 };
  }, [report]);

  const openDrill = (title, kind, items) => { if (items && items.length) setDrill({ title, kind, items }); };
  const clearAll = () => { setGroup(""); setFrom(""); setTo(""); setPeriod(""); };

  if (rows === null) return <div className="empty"><Spinner /></div>;

  const recordsById = {};
  (rows || []).forEach((r) => { recordsById[r.id] = r; });
  if (drill) return <ProductivityDrill title={drill.title} items={drill.items} recordsById={recordsById} onBack={() => setDrill(null)} />;

  const ClkCell = ({ n, cls, items, title }) => (
    n && items && items.length
      ? <td className={"prod-clk " + (cls || "")} onClick={() => openDrill(title, "status", items)}>{n}</td>
      : <td className={cls}>{n || "—"}</td>
  );
  const IssueCell = ({ n, items, title, first }) => (
    <td className={"prod-mod" + (first ? " prod-mod-first sec-issue" : "")}>
      {n && items && items.length
        ? <button type="button" className="prod-inner-clk" onClick={() => openDrill(title, "issue", items)}>{n}</button>
        : (n || "—")}
    </td>
  );

  return (
    <div className="prod myp">
      <div className="prod-filters">
        <div className="prod-field">
          <span className="prod-flabel">Period</span>
          <select className="inp" value={period} onChange={(e) => { setPeriod(e.target.value); if (e.target.value !== "custom") { setFrom(""); setTo(""); } }}>
            <option value="">All time</option>
            {QUICKS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            <option value="custom">Custom range…</option>
          </select>
        </div>
        <div className="prod-field"><span className="prod-flabel">From</span>
          <input className="inp" type="date" value={from} max={to || undefined} onChange={(e) => { setFrom(e.target.value); setPeriod("custom"); }} /></div>
        <div className="prod-field"><span className="prod-flabel">To</span>
          <input className="inp" type="date" value={to} min={from || undefined} onChange={(e) => { setTo(e.target.value); setPeriod("custom"); }} /></div>
        <button className="link-btn prod-clear" onClick={clearAll}>Clear</button>
        <button className="btn btn-ghost btn-sm" onClick={() => load()}
          title="Auto-refreshes every 10 seconds — click to refresh now"
          style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#10b981", display: "inline-block" }} /> ↻ Refresh · Live every 10s
        </button>
      </div>

      <div className="card prod-card"><div className="table-wrap">
        <table className="tbl prod-tbl">
          <thead>
            <tr>
              {bucketMode && <th rowSpan="2" className="prod-usercol">{bucketMode === "day" ? "Date" : "Month"}</th>}
              <th rowSpan="2" className="prod-usercol">User</th>
              <th colSpan="4" className="prod-grouphead sec-work">Work done</th>
              <th colSpan="8" className="prod-grouphead sec-status">Status</th>
              <th colSpan="3" className="prod-grouphead sec-issue">Modifications</th>
              <th rowSpan="2" className="prod-pct sec-pct">Approval %</th>
            </tr>
            <tr>
              <th className="sec-work">SUB</th><th>EP</th><th>MOD</th><th>MI</th>
              <th className="sec-status">STAT</th><th>Approved</th><th>PA N/R</th><th>Denied</th><th>Pending</th><th>Canceled</th><th>Withdraw</th><th>Reviewed</th>
              <th className="sec-issue">Client</th><th>Patient</th><th>Production</th>
            </tr>
          </thead>
          <tbody>
            {bucketMode ? (
              report.length === 0
                ? <tr><td colSpan={17} className="prod-empty">No activity for these filters.</td></tr>
                : report.map((r) => (
                  <tr key={r.bucket}>
                    <td className="cell-sub">{bucketLabel(r.bucket)}</td>
                    <td className="prod-usercol"><b>{me || "You"}</b></td>
                    <td className="sec-work">{r.submissions || "—"}</td><td>{r.endProcess || "—"}</td><td>{r.modifications || "—"}</td><td>{r.moreInfo || "—"}</td>
                    <td className="sec-status">{r.stat || "—"}</td><td className="pos">{r.approved || "—"}</td><td className="pos">{r.paNotReq || "—"}</td>
                    <ClkCell n={r.denied} cls="neg" items={r.deniedL} title="Your denied records" />
                    <td>{r.pending || "—"}</td>
                    <ClkCell n={r.canceled} items={r.canceledL} title="Your canceled records" />
                    <ClkCell n={r.withdraw} items={r.withdrawL} title="Your withdrawn records" />
                    <td>{fmtRev(r.reviewed)}</td>
                    <IssueCell first n={r.clientIssue} items={r.clientL} title="Your client issues" />
                    <IssueCell n={r.patientIssue} items={r.patientL} title="Your patient issues" />
                    <IssueCell n={r.productionIssue} items={r.productionL} title="Your production issues" />
                    <td className="prod-pct sec-pct"><b>{r.approvalPct.toFixed(1)}%</b></td>
                  </tr>
                ))
            ) : (
              <tr>
                <td className="prod-usercol"><b>{me || "You"}</b></td>
                <td className="sec-work">{s.submissions || "—"}</td><td>{s.endProcess || "—"}</td><td>{s.modifications || "—"}</td><td>{s.moreInfo || "—"}</td>
                <td className="sec-status">{s.stat || "—"}</td><td className="pos">{s.approved || "—"}</td><td className="pos">{s.paNotReq || "—"}</td>
                <ClkCell n={s.denied} cls="neg" items={s.deniedL} title="Your denied records" />
                <td>{s.pending || "—"}</td>
                <ClkCell n={s.canceled} items={s.canceledL} title="Your canceled records" />
                <ClkCell n={s.withdraw} items={s.withdrawL} title="Your withdrawn records" />
                <td>{fmtRev(s.reviewed)}</td>
                <IssueCell first n={s.clientIssue} items={s.clientL} title="Your client issues" />
                <IssueCell n={s.patientIssue} items={s.patientL} title="Your patient issues" />
                <IssueCell n={s.productionIssue} items={s.productionL} title="Your production issues" />
                <td className="prod-pct sec-pct"><b>{s.approvalPct.toFixed(1)}%</b></td>
              </tr>
            )}
          </tbody>
        </table>
      </div></div>

      <p className="prod-note">Every round of work carries a status, so the Status columns add up to the work done in SUB + EP + MOD + MI. (Records submitted by fax or call — statuses Faxed / Submitted via call — count as Pending, submitted and awaiting a response.) <b>Reviewed</b> is shown as an effort score (1 each time you keep a record in Reviewed through submission or end-process work, plus 0.25 each time you later open an already-kept reviewed record and mark it — including when you approve/deny it that day), so it may not be a whole number. Approval % = Approved ÷ (Approved + Denied + Pending + Canceled + Withdraw + Reviewed) × 100. Click a <b>Denied / Canceled / Withdraw</b> or an <b>issue</b> number to see those records with their reasons and remarks.</p>
    </div>
  );
}